import { Box, Button, Dialog, IconButton, Stack, Switch, Typography } from '@mui/material';
import { FilterKeys, GetAssessmentsQueryFilters, RepetitionType } from '@schooly/api';
import { SchoolYearSelect } from '@schooly/components/filters';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../../hooks/useSchool';
import useSchoolYears from '../../../../hooks/useSchoolYears';

type SelectAssessmentFiltersPopupProps = {
  filters: GetAssessmentsQueryFilters;
  defaultFilters: GetAssessmentsQueryFilters;
  onClose: () => void;
  onSetFilters: (v: GetAssessmentsQueryFilters) => void;
};

export const SelectAssessmentFiltersPopup: FC<SelectAssessmentFiltersPopupProps> = ({
  onClose,
  onSetFilters,
  filters: initialFilters,
  defaultFilters,
}) => {
  const { formatMessage } = useIntl();
  const { schoolYears } = useSchoolYears();
  const { schoolId } = useSchool();
  const [filters, setFilters] = useState<GetAssessmentsQueryFilters>(initialFilters);

  const showRecurringAssessments = filters[FilterKeys.RepetitionType]?.includes(
    RepetitionType.FirstOfRecurrence,
  );
  const schoolYear = schoolYears.find(
    ({ start, end }) =>
      start === filters[FilterKeys.Date]?.[0] && end === filters[FilterKeys.Date]?.[1],
  );

  const handleApply = useCallback(() => {
    onSetFilters(filters);
    onClose();
  }, [onSetFilters, filters, onClose]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const { onSelectYear, onToggleRecurringType } = useMemo(() => {
    const onSelectYear = (id: string) => {
      const year = schoolYears.find(({ id: yearId }) => yearId === id);
      if (!year) return;
      setFilters((v) => ({ ...v, [FilterKeys.Date]: [year.start, year.end] }));
    };

    const onToggleRecurringType = (checked: boolean) => {
      setFilters((v) => ({
        ...v,
        [FilterKeys.RepetitionType]: checked
          ? [RepetitionType.FirstOfRecurrence, RepetitionType.NotRecurring]
          : [RepetitionType.NotRecurring],
      }));
    };

    return {
      onSelectYear,
      onToggleRecurringType,
    };
  }, [schoolYears]);

  if (!schoolId) return null;
  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 700,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'reports-FilterAssessments' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        <Stack gap={1}>
          <FilterRow label={formatMessage({ id: 'schoolYear' })}>
            <SchoolYearSelect
              borderOnHover
              selectedId={schoolYear?.id ?? ''}
              onSelect={onSelectYear}
            />
          </FilterRow>

          <FilterRow
            label={formatMessage(
              { id: 'recurring-showRecurringEntity' },
              { entityName: formatMessage({ id: 'assessments-Assessments' }) },
            )}
          >
            <Switch
              checked={showRecurringAssessments}
              onChange={(e) => onToggleRecurringType(e.target.checked)}
              sx={{ ml: 1.5 }}
            />
          </FilterRow>
        </Stack>
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};

const FilterRow: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <Stack flexDirection="row" alignItems="center" gap={2}>
    <Box sx={{ flex: '1 1 25%' }}>
      <Typography color="common.grey2">{label}</Typography>
    </Box>
    <Box sx={{ flex: '1 1 75%' }}>{children}</Box>
  </Stack>
);
